import { createSlice } from '@reduxjs/toolkit';
import { notifyUserError, notifyUserSuccess } from 'utils/notifications';

import {
  deleteClinicalEncounters,
  getClinicalEncounters,
  getClinicalEncountersPreview,
  getClinicalEncountersTypes,
  getClinicalEncountersStatuses,
  postClinicalEncounters,
  putClinicalEncounters,
  ClinicalEncountersItem,
  ClinicalEncountersFilters
} from './clinicalEncountersActions';

type SelectItem = {
  id: number;
  name: string;
};

type InitialState = {
  clinicalEncountersTypes: SelectItem[];
  clinicalEncountersStatuses: SelectItem[];
  clinicalEncountersList: {
    content: ClinicalEncountersItem[];
    page: number;
    size: number;
    sorts: { title: string; direction: string }[] | null;
    filters: ClinicalEncountersFilters | null;
  };
  clinicalEncountersListLoading: boolean;
  clinicalEncountersPreviewList: Pick<
    ClinicalEncountersItem,
    'id' | 'location' | 'dateOfEncounter'
  >[];
  clinicalEncountersPreviewListLoading: boolean;
};

const initialState: InitialState = {
  clinicalEncountersPreviewList: [],
  clinicalEncountersPreviewListLoading: false,
  clinicalEncountersTypes: [],
  clinicalEncountersStatuses: [],
  clinicalEncountersList: {
    content: [],
    page: 0,
    size: 99999,
    sorts: null,
    filters: null
  },
  clinicalEncountersListLoading: false
};

export const clinicalEncountersSlice = createSlice({
  name: 'clinicalEncountersSlice',
  initialState,
  reducers: {
    updateSortsList: (state, action) => {
      state.clinicalEncountersList.sorts = action.payload;
    },
    resetClinicalEncountersList: (state) => {
      state.clinicalEncountersTypes = [];
      state.clinicalEncountersStatuses = [];
      state.clinicalEncountersListLoading = false;
      state.clinicalEncountersList = {
        content: [],
        page: 0,
        size: 99999,
        sorts: null,
        filters: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClinicalEncountersPreview.pending, (state, action) => {
        state.clinicalEncountersPreviewListLoading = true;
      })
      .addCase(getClinicalEncountersPreview.fulfilled, (state, action) => {
        state.clinicalEncountersPreviewList = action.payload.data;
        state.clinicalEncountersPreviewListLoading = false;
      })
      .addCase(getClinicalEncountersPreview.rejected, (state, { payload }: any) => {
        state.clinicalEncountersPreviewListLoading = false;
        notifyUserError(payload);
      })
      .addCase(getClinicalEncounters.pending, (state, action) => {
        state.clinicalEncountersListLoading = true;
      })
      .addCase(getClinicalEncounters.fulfilled, (state, action) => {
        state.clinicalEncountersList = action.payload;
        state.clinicalEncountersListLoading = false;
      })
      .addCase(getClinicalEncounters.rejected, (state, { payload }: any) => {
        state.clinicalEncountersListLoading = false;
        notifyUserError(payload);
      })
      .addCase(postClinicalEncounters.fulfilled, (state, { payload }: any) => {
        notifyUserSuccess('Your encounter was saved');
      })
      .addCase(postClinicalEncounters.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putClinicalEncounters.fulfilled, (state, { payload }: any) => {
        const index = state.clinicalEncountersList.content.findIndex(
          (a) => a.id === payload.data?.id
        );
        const type = state.clinicalEncountersTypes.find((t) => t.id === payload.data?.typeId)?.name;
        const status = state.clinicalEncountersStatuses.find(
          (s) => s.id === payload.data?.statusId
        )?.name;
        if (index !== -1) {
          state.clinicalEncountersList.content[index] = { ...payload.data, type, status };
        }
      })
      .addCase(putClinicalEncounters.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(deleteClinicalEncounters.fulfilled, (state, { payload }: any) => {
        const index = state.clinicalEncountersList.content.findIndex((a) => a.id === payload);
        if (index !== -1) {
          state.clinicalEncountersList.content.splice(index, 1);
        }
      })
      .addCase(deleteClinicalEncounters.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getClinicalEncountersTypes.fulfilled, (state, action) => {
        state.clinicalEncountersTypes = action.payload;
      })
      .addCase(getClinicalEncountersTypes.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getClinicalEncountersStatuses.fulfilled, (state, action) => {
        state.clinicalEncountersStatuses = action.payload;
      })
      .addCase(getClinicalEncountersStatuses.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      });
  }
});

export const { updateSortsList, resetClinicalEncountersList } = clinicalEncountersSlice.actions;
export default clinicalEncountersSlice.reducer;
